// The Categories tab of Products (docs/plans/categories.md): every category with its
// products, what is low or out and what the stock is worth; rename, move the products to
// another category, and delete. Board `CatTab` / `CatTabPhone` / `CatDelete`.
// In a restaurant (`menu`) the categories are the menu's sections: they are listed in the
// guest menu's order, count the items on the menu, and "Change order" sets that order
// (docs/plans/menu-sections.md, boards `MenuCats`, `MenuCatsOrder`, `MenuCatsPhone`).
import type { ProductRow, ProductSummary, RowOf } from '@ims/data'
import {
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  IconButton,
  IconTile,
  Input,
  Kbd,
  ListFrame,
  ListToolbar,
  Menu,
  MenuItem,
  Modal,
  Pagination,
  RowActions,
  RowNote,
  RowTitle,
  SearchInput,
  Select,
  SkeletonTable,
  TD,
  TH,
  TR,
  Table,
  cx,
  useToast,
} from '@ims/ui'
import {
  ArrowDown,
  ArrowUp,
  ArrowsLeftRight,
  CookingPot,
  FolderSimple,
  GripVertical,
  MoreHorizontal,
  Pencil,
  QrCode,
  Trash2,
  Wine,
} from '@ims/ui/icons'
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { useLookups } from '../components.tsx'
import { errorMessage, useLive, useSettings } from '../hooks.ts'
import {
  categoryStanding,
  dropInList,
  menuOrder,
  moveInList,
  nameClash,
  nextSortOrder,
  planCategoryMove,
  reorderOps,
} from '../lib/categories.ts'
import { money } from '../lib/format.ts'
import { useListKeys } from '../lib/list-keys.ts'
import { usePaged } from '../lib/paging.ts'
import { insertOp, newCategory, updateOp } from '../lib/rows.ts'
import { isSoldOut } from '../lib/sold-out.ts'
import { useCan, useRepo, useWorkspace } from '../workspace.tsx'

type Category = RowOf<'categories'>
type Numbers = ProductSummary['categories'][number]

const ROW_BUTTON = 'h-[34px] text-[13px]'
const ROW_ICON =
  'size-[34px] border border-border bg-surface text-ink max-sm:size-10! max-sm:flex-none!'
/** The row the arrow keys are on. */
const ROW_ACTIVE = 'sm:bg-stock-wash sm:shadow-[inset_3px_0_0_var(--color-stock)]'
/** The row for products without a category. */
const NONE = 'none'

/** Where a category's products are listed. */
export const categoryPath = (id: string) => `/products?category=${id}`

/** The header line of the Categories tab: how many, how many products have none, how many empty. */
export function categoriesSubtitle(summary: ProductSummary | null | undefined, count: number) {
  const without = summary?.categories.find((c) => c.categoryId === null)?.count ?? 0
  const used = new Set(summary?.categories.filter((c) => c.count > 0).map((c) => c.categoryId))
  return { without, empty: summary ? count - [...used].filter(Boolean).length : 0 }
}

/** A menu section's items: on the menu, hidden, sold out today, and where they are made. */
interface MenuNumbers {
  items: number
  onMenu: number
  hidden: number
  soldOut: number
  kitchen: boolean
  bar: boolean
}

const NO_ITEMS: MenuNumbers = {
  items: 0,
  onMenu: 0,
  hidden: 0,
  soldOut: 0,
  kitchen: false,
  bar: false,
}

/** The menu's items by category (a variant group counts once, as on the Menu list). */
function useMenuNumbers(on: boolean): Map<string | null, MenuNumbers> | undefined {
  const sold = useLive(
    async (repo) =>
      on ? (await repo.list('products', { where: { for_sale: true }, limit: 1_000_000 })).rows : [],
    [on],
    ['products'],
  ).data
  if (!sold) return undefined
  const now = new Date()
  const out = new Map<string | null, MenuNumbers>()
  for (const p of sold as ProductRow[]) {
    if (p.kind === 'variant') continue
    const n = { ...(out.get(p.category_id) ?? NO_ITEMS) }
    n.items++
    if (!p.is_active) n.hidden++
    else {
      n.onMenu++
      if (isSoldOut(p, now)) n.soldOut++
    }
    if (p.kitchen_station === 'kitchen') n.kitchen = true
    if (p.kitchen_station === 'bar') n.bar = true
    out.set(p.category_id, n)
  }
  return out
}

/** "9 on the menu · 1 hidden · 1 sold out today". */
function menuLine(n: MenuNumbers): ReactNode {
  if (n.items === 0) return <span className="text-muted">No items yet</span>
  return (
    <span className="text-sm text-muted">
      <b className="font-semibold text-ink">{n.onMenu} on the menu</b>
      {n.hidden > 0 && ` · ${n.hidden} hidden`}
      {n.soldOut > 0 && (
        <span className="font-semibold text-warning"> · {n.soldOut} sold out today</span>
      )}
    </span>
  )
}

/** Where a section's items are made. */
function GoesTo({ n }: { n: MenuNumbers }) {
  if (!n.kitchen && !n.bar) return <span className="text-muted">—</span>
  const Icon = n.kitchen ? CookingPot : Wine
  return (
    <span className="flex items-center gap-1.5 text-[13px] text-muted">
      <Icon className="size-4" aria-hidden />
      {n.kitchen && n.bar ? 'Kitchen and bar' : n.kitchen ? 'Kitchen' : 'Bar'}
    </span>
  )
}

type Dialog =
  | { kind: 'name'; category: Category | null }
  | { kind: 'move'; category: Category; remove: boolean; to?: string }
  | { kind: 'delete-empty'; category: Category }

export function CategoriesTab({
  summary,
  adding,
  onAddingDone,
  menu = false,
  ordering = false,
  onOrderingDone,
}: {
  summary: ProductSummary | null | undefined
  /** "New category" in the page header was pressed. */
  adding: boolean
  onAddingDone: () => void
  /** A restaurant's menu sections: in menu order, counting the items on the menu. */
  menu?: boolean
  /** "Change order" in the page header was pressed (menu only). */
  ordering?: boolean
  onOrderingDone?: () => void
}) {
  const settings = useSettings()
  const lookups = useLookups()
  const navigate = useNavigate()
  const canEdit = useCan('staff')
  const canManage = useCan('manager')
  const [search, setSearch] = useState('')
  const [active, setActive] = useState<string | null>(null)
  const [dialog, setDialogValue] = useState<Dialog | null>(null)
  const shown: Dialog | null = adding ? { kind: 'name', category: null } : dialog
  const setDialog = (d: Dialog | null) => {
    if (!d && adding) onAddingDone()
    setDialogValue(d)
  }

  const menuNumbers = useMenuNumbers(menu)
  const items = (id: string | null): MenuNumbers => menuNumbers?.get(id) ?? NO_ITEMS
  const ordered = menu ? menuOrder(lookups.categories) : lookups.categories
  const position = new Map(ordered.map((c, i) => [c.id, i + 1]))
  const numbers = new Map<string | null, Numbers>(
    (summary?.categories ?? []).map((c) => [c.categoryId, c]),
  )
  const of = (id: string | null): Numbers =>
    numbers.get(id) ?? { categoryId: id, count: 0, low: 0, out: 0, valuePaisa: 0 }
  const needle = search.trim().toLowerCase()
  const without = menu ? items(null).items : of(null).count
  const rows: { id: string; category: Category | null; name: string }[] = [
    ...ordered
      .filter((c) => !needle || c.name.toLowerCase().includes(needle))
      .map((c) => ({ id: c.id, category: c, name: c.name })),
    // Products without a category, last; only while there are some.
    ...(without > 0 && (!needle || 'no category'.includes(needle))
      ? [{ id: NONE, category: null, name: 'No category' }]
      : []),
  ]
  const paged = usePaged(rows, search)
  const list = useRef<HTMLDivElement>(null)
  const activeRow = paged.rows.find((r) => r.id === active)
  useListKeys({
    enabled: !ordering,
    ids: paged.rows.map((r) => r.id),
    active,
    setActive,
    page: paged,
    list,
    open: () => activeRow && navigate(categoryPath(activeRow.id)),
    keys: {
      r: () =>
        canEdit && activeRow?.category && setDialog({ kind: 'name', category: activeRow.category }),
    },
  })

  if (menu && ordering && onOrderingDone) {
    return <OrderCategories categories={ordered} items={items} onDone={onOrderingDone} />
  }

  return (
    <>
      <ListToolbar>
        <SearchInput
          listKeys
          className="max-sm:w-full sm:w-80"
          value={search}
          onChange={setSearch}
          placeholder="Category name…"
        />
      </ListToolbar>
      <ListFrame ref={list}>
        {!lookups.loaded ? (
          <SkeletonTable cols={menu ? 4 : 5} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<FolderSimple className="size-6" />}
            title={needle ? 'No categories match' : 'No categories yet'}
            description={
              needle
                ? undefined
                : menu
                  ? 'Group your dishes into sections, as guests and waiters will see them.'
                  : 'Group your products, then find them by category in the list.'
            }
            action={
              canEdit &&
              !needle && (
                <Button onClick={() => setDialog({ kind: 'name', category: null })}>
                  New category
                </Button>
              )
            }
          />
        ) : (
          <>
            <Table stack="cards">
              <thead>
                {menu ? (
                  <tr>
                    <TH className="w-12">#</TH>
                    <TH>Category</TH>
                    <TH>Items</TH>
                    <TH>Goes to</TH>
                    <TH />
                  </tr>
                ) : (
                  <tr>
                    <TH>Category</TH>
                    <TH align="right">Products</TH>
                    <TH>Where it stands</TH>
                    <TH align="right">Stock value</TH>
                    <TH />
                  </tr>
                )}
              </thead>
              <tbody>
                {paged.rows.map((r) => {
                  const n = of(r.category ? r.id : null)
                  const m = items(r.category ? r.id : null)
                  // What the row holds, for the actions: products, or a menu's items.
                  const count = menu ? m.items : n.count
                  const to = categoryPath(r.id)
                  const c = r.category
                  return (
                    <TR
                      key={r.id}
                      onClick={() => navigate(to)}
                      aria-current={active === r.id ? 'true' : undefined}
                      className={cx(!c && 'sm:bg-soft/60', active === r.id && ROW_ACTIVE)}
                    >
                      {menu && (
                        <TD className="tabular w-12 font-bold text-muted max-sm:hidden">
                          {c ? position.get(c.id) : ''}
                        </TD>
                      )}
                      <TD className="stack-title">
                        <RowTitle
                          tile={
                            <IconTile area={c ? 'stock' : 'neutral'} size="sm">
                              <FolderSimple className="size-4" />
                            </IconTile>
                          }
                          title={
                            <Link
                              to={to}
                              className="hover:underline focus-visible:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {r.name}
                            </Link>
                          }
                          sub={
                            menu
                              ? c
                                ? `${m.items} item${m.items === 1 ? '' : 's'}`
                                : 'Shown last on the menu, under “More”'
                              : c
                                ? n.count === 0
                                  ? 'No products yet'
                                  : `${n.count} product${n.count === 1 ? '' : 's'}`
                                : 'Not in a category yet'
                          }
                        />
                      </TD>
                      {menu ? (
                        <>
                          <TD className="stack-r3">{menuNumbers && menuLine(m)}</TD>
                          <TD className="max-sm:hidden">{menuNumbers && <GoesTo n={m} />}</TD>
                        </>
                      ) : (
                        <>
                          <TD align="right" className="tabular font-semibold max-sm:hidden">
                            {summary ? n.count : '—'}
                          </TD>
                          <TD className="stack-r3">
                            {summary && <RowNote {...categoryStanding(n)} />}
                          </TD>
                          <TD
                            align="right"
                            className="stack-end stack-r1 tabular whitespace-nowrap font-bold"
                          >
                            {/* No cost prices yet: nothing to add up. */}
                            {summary && n.count > 0
                              ? n.valuePaisa > 0
                                ? money(n.valuePaisa, settings)
                                : '—'
                              : ''}
                          </TD>
                        </>
                      )}
                      <TD className="stack-actions sm:w-px sm:whitespace-nowrap">
                        <RowActions>
                          {c ? (
                            <>
                              {canEdit && (
                                <IconButton
                                  label={`Rename ${c.name}`}
                                  className={ROW_ICON}
                                  onClick={() => setDialog({ kind: 'name', category: c })}
                                >
                                  <Pencil className="size-4" />
                                </IconButton>
                              )}
                              {canManage && (summary || menuNumbers) && count === 0 && (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  className={cx(ROW_BUTTON, 'text-danger')}
                                  icon={<Trash2 className="size-4" />}
                                  aria-label={`Delete ${c.name}`}
                                  onClick={() => setDialog({ kind: 'delete-empty', category: c })}
                                >
                                  Delete
                                </Button>
                              )}
                              {canManage && count > 0 && (
                                <Menu
                                  label={`More for ${c.name}`}
                                  trigger={<MoreHorizontal className="size-4" />}
                                  triggerClassName={cx(
                                    'inline-flex items-center justify-center rounded-xl hover:bg-soft',
                                    ROW_ICON,
                                  )}
                                  width={220}
                                >
                                  <MenuItem
                                    icon={<ArrowsLeftRight className="size-4" />}
                                    onSelect={() =>
                                      setDialog({ kind: 'move', category: c, remove: false })
                                    }
                                  >
                                    {menu ? 'Move items to…' : 'Move products to…'}
                                  </MenuItem>
                                  <MenuItem
                                    danger
                                    icon={<Trash2 className="size-4" />}
                                    onSelect={() =>
                                      setDialog({ kind: 'move', category: c, remove: true })
                                    }
                                  >
                                    Delete…
                                  </MenuItem>
                                </Menu>
                              )}
                            </>
                          ) : (
                            <Button
                              size="sm"
                              variant="soft"
                              className={ROW_BUTTON}
                              onClick={() => navigate(to)}
                            >
                              Give them a category
                            </Button>
                          )}
                        </RowActions>
                      </TD>
                    </TR>
                  )
                })}
              </tbody>
            </Table>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-xs text-muted max-sm:px-1">
              <span>
                {menu
                  ? 'In the order guests and waiters see them. A row lists its items.'
                  : 'A to Z. A row lists its products. Stock value is at cost.'}
              </span>
              <span className="ml-auto flex items-center gap-1.5 max-lg:hidden">
                <Kbd>↑</Kbd> <Kbd>↓</Kbd> move <Kbd>Enter</Kbd> open{' '}
                {canEdit && (
                  <>
                    <Kbd>R</Kbd> rename
                  </>
                )}
              </span>
            </div>
            <Pagination
              offset={paged.offset}
              limit={paged.limit}
              total={paged.total}
              onChange={paged.setOffset}
            />
          </>
        )}
      </ListFrame>
      {shown?.kind === 'name' && (
        <NameDialog
          category={shown.category}
          categories={lookups.categories}
          menu={menu}
          canMerge={
            canManage &&
            !!shown.category &&
            (menu ? items(shown.category.id).items : of(shown.category.id).count) > 0
          }
          onMerge={(to) =>
            shown.category &&
            setDialog({ kind: 'move', category: shown.category, remove: true, to })
          }
          onClose={() => setDialog(null)}
        />
      )}
      {shown?.kind === 'move' && (
        <MoveDialog
          category={shown.category}
          count={menu ? items(shown.category.id).items : of(shown.category.id).count}
          remove={shown.remove}
          initialTo={shown.to}
          categories={lookups.categories}
          onClose={() => setDialog(null)}
        />
      )}
      {shown?.kind === 'delete-empty' && (
        <DeleteEmptyDialog category={shown.category} onClose={() => setDialog(null)} />
      )}
    </>
  )
}

/** A new category, or a new name for one. A name in use offers to move into that one. */
function NameDialog({
  category,
  categories,
  menu,
  canMerge,
  onMerge,
  onClose,
}: {
  category: Category | null
  categories: readonly Category[]
  /** A new menu section goes after the last one. */
  menu: boolean
  canMerge: boolean
  onMerge: (to: string) => void
  onClose: () => void
}) {
  const repo = useRepo()
  const toast = useToast()
  const { current } = useWorkspace()
  const [name, setName] = useState(category?.name ?? '')
  const [busy, setBusy] = useState(false)
  const clash = nameClash(name, categories, category?.id)
  const unchanged = !!category && name.trim() === category.name
  const save = async () => {
    const value = name.trim()
    if (!value || clash || unchanged || !current) return
    setBusy(true)
    try {
      await repo.commit([
        category
          ? updateOp('categories', category, { name: value })
          : insertOp(
              'categories',
              newCategory(
                { tenantId: current.tenant_id },
                { name: value, ...(menu ? { sort_order: nextSortOrder(categories) } : {}) },
              ),
            ),
      ])
      toast.success(category ? 'Category renamed' : `Added ${value}`)
      onClose()
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }
  return (
    <Modal
      open
      onClose={onClose}
      title={category ? `Rename ${category.name}` : 'New category'}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={!name.trim() || !!clash || unchanged}
            onClick={() => void save()}
          >
            {category ? 'Save' : 'Add category'}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void save()
        }}
      >
        <Field label="Name" error={clash ? `${clash.name} already exists.` : null}>
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        {clash && canMerge && (
          <Button
            type="button"
            variant="soft"
            className="mt-3"
            icon={<ArrowsLeftRight className="size-4" />}
            onClick={() => onMerge(clash.id)}
          >
            Move the products into {clash.name}
          </Button>
        )}
      </form>
    </Modal>
  )
}

/**
 * Moves a category's products to another category or to none; with `remove`, deletes the
 * category after (in the last commit, `planCategoryMove`).
 */
function MoveDialog({
  category,
  count,
  remove,
  initialTo,
  categories,
  onClose,
}: {
  category: Category
  count: number
  remove: boolean
  initialTo?: string
  categories: readonly Category[]
  onClose: () => void
}) {
  const repo = useRepo()
  const toast = useToast()
  const others = categories.filter((c) => c.id !== category.id)
  const [where, setWhere] = useState<'move' | 'none'>(others.length > 0 ? 'move' : 'none')
  const [to, setTo] = useState(initialTo ?? others[0]?.id ?? '')
  const [busy, setBusy] = useState(false)
  const target = where === 'move' ? to || null : null
  const go = async () => {
    if (where === 'move' && !to) return
    setBusy(true)
    try {
      // Every product of the category as it is now, variants included.
      const products = (
        await repo.list('products', { where: { category_id: category.id }, limit: 1_000_000 })
      ).rows
      const plan = planCategoryMove(category.id, products, target, remove)
      for (const ops of plan.chunks) if (ops.length > 0) await repo.commit(ops)
      const into = target ? `to ${others.find((c) => c.id === target)?.name}` : 'out of it'
      toast.success(
        remove ? `${category.name} deleted` : `Moved ${plan.moved} ${into}`,
        remove && plan.moved > 0 ? { detail: `${plan.moved} products moved ${into}.` } : undefined,
      )
      onClose()
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }
  // The extra (a select) sits outside the radio's label, so each control keeps its own name.
  const option = (value: 'move' | 'none', label: string, extra: ReactNode) => (
    <div
      className={cx(
        'rounded-xl border p-3',
        where === value ? 'border-primary bg-sell-wash' : 'border-border',
      )}
    >
      <label className="flex cursor-pointer items-start gap-3">
        <input
          type="radio"
          name="where"
          className="mt-1 accent-primary"
          checked={where === value}
          onChange={() => setWhere(value)}
        />
        <span className="font-semibold">{label}</span>
      </label>
      <div className="pl-7">{extra}</div>
    </div>
  )
  return (
    <Modal
      open
      onClose={onClose}
      title={remove ? `Delete ${category.name}?` : `Move the products of ${category.name}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {remove ? 'Keep it' : 'Cancel'}
          </Button>
          <Button
            variant={remove ? 'danger' : 'primary'}
            loading={busy}
            disabled={where === 'move' && !to}
            onClick={() => void go()}
          >
            {remove ? `Move ${count} and delete` : `Move ${count}`}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-muted">
          {count} product{count === 1 ? ' is' : 's are'} in it.
          {remove ? ' Choose where they go first.' : ' Choose where they go.'}
        </p>
        {others.length > 0 &&
          option(
            'move',
            'Move them to another category',
            <Select
              aria-label="Move them to"
              className="mt-2"
              value={to}
              onChange={(e) => {
                setTo(e.target.value)
                setWhere('move')
              }}
            >
              {others.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>,
          )}
        {option(
          'none',
          'Leave them without a category',
          <span className="block text-xs text-muted">
            They stay on sale; only the filter changes.
          </span>,
        )}
        <p className="text-xs text-muted">Bills and stock are not touched. Works offline.</p>
      </div>
    </Modal>
  )
}

function DeleteEmptyDialog({ category, onClose }: { category: Category; onClose: () => void }) {
  const repo = useRepo()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  return (
    <ConfirmDialog
      open
      title={`Delete ${category.name}?`}
      message="No products are in it."
      confirmLabel="Delete"
      danger
      busy={busy}
      onClose={onClose}
      onConfirm={async () => {
        setBusy(true)
        try {
          for (const ops of planCategoryMove(category.id, [], null, true).chunks)
            await repo.commit(ops)
          toast.success(`${category.name} deleted`)
          onClose()
        } catch (error) {
          toast.error(errorMessage(error))
        } finally {
          setBusy(false)
        }
      }}
    />
  )
}

/**
 * "Change order" (board `MenuCatsOrder`): drag a row, or use its arrows (Alt ↑ and Alt ↓ on
 * a keyboard); the card beside it shows what guests and waiters will see. Saving numbers the
 * sections 1..n in one commit (`reorderOps`), only the rows that change.
 */
function OrderCategories({
  categories,
  items,
  onDone,
}: {
  categories: readonly Category[]
  items: (id: string) => MenuNumbers
  onDone: () => void
}) {
  const repo = useRepo()
  const toast = useToast()
  const [ids, setIds] = useState(() => categories.map((c) => c.id))
  const [dragging, setDragging] = useState<string | null>(null)
  const [over, setOver] = useState<string | null>(null)
  const [focus, setFocus] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const rows = useRef(new Map<string, HTMLLIElement>())
  const byId = new Map(categories.map((c) => [c.id, c]))
  const changed = reorderOps(categories, ids).length > 0
  const moved = categories.map((c) => c.id).join() !== ids.join()

  // A row moved from the keyboard keeps the focus after it is redrawn.
  useEffect(() => {
    if (focus) rows.current.get(focus)?.focus()
  }, [focus, ids])

  const move = (id: string, delta: number) => setIds((list) => moveInList(list, id, delta))
  const onKey = (e: KeyboardEvent, id: string) => {
    if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return
    e.preventDefault()
    move(id, e.key === 'ArrowUp' ? -1 : 1)
    setFocus(id)
  }
  const save = async () => {
    const ops = reorderOps(categories, ids)
    if (ops.length === 0) return onDone()
    setBusy(true)
    try {
      await repo.commit(ops)
      toast.success('Menu order saved', {
        detail: 'The QR menu and the order screen show the new order.',
      })
      onDone()
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-primary/30 bg-sell-wash px-4 py-3">
        <p className="min-w-0 flex-1 text-sm">
          <b>Change the order.</b>{' '}
          <span className="text-muted">
            Drag a row, or use its arrows
            <span className="max-lg:hidden">; on a keyboard, Alt ↑ and Alt ↓</span>.
          </span>
        </p>
        <Button variant="secondary" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button size="sm" loading={busy} disabled={!changed && !moved} onClick={() => void save()}>
          Save order
        </Button>
      </div>
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <ListFrame>
          <ol aria-label="Categories in menu order" className="max-sm:space-y-2">
            {ids.map((id, i) => {
              const c = byId.get(id)
              if (!c) return null
              const m = items(id)
              return (
                <li
                  key={id}
                  ref={(el) => {
                    if (el) rows.current.set(id, el)
                    else rows.current.delete(id)
                  }}
                  tabIndex={0}
                  aria-label={`${i + 1}. ${c.name}`}
                  draggable
                  onDragStart={(e) => {
                    setDragging(id)
                    e.dataTransfer.effectAllowed = 'move'
                  }}
                  onDragOver={(e) => {
                    if (!dragging) return
                    e.preventDefault()
                    setOver(id)
                  }}
                  onDrop={(e) => {
                    e.preventDefault()
                    if (dragging && dragging !== id)
                      setIds((list) => dropInList(list, dragging, id))
                    setDragging(null)
                    setOver(null)
                  }}
                  onDragEnd={() => {
                    setDragging(null)
                    setOver(null)
                  }}
                  onKeyDown={(e) => onKey(e, id)}
                  className={cx(
                    'flex items-center gap-3 bg-surface px-3 py-2.5 outline-none sm:border-b sm:border-border/60 sm:px-4',
                    'max-sm:rounded-xl max-sm:border max-sm:border-border max-sm:shadow-card',
                    'focus-visible:ring-2 focus-visible:ring-primary/40',
                    dragging === id && 'opacity-50',
                    over === id && dragging !== id && 'shadow-[inset_0_2px_0_var(--color-primary)]',
                  )}
                >
                  <GripVertical
                    className="size-5 shrink-0 cursor-grab text-subtle max-sm:hidden"
                    aria-hidden
                  />
                  <span className="tabular w-6 shrink-0 text-center font-bold text-muted">
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <b className="block truncate">{c.name}</b>
                    <span className="block text-[13px] text-muted">
                      {m.items} item{m.items === 1 ? '' : 's'}
                    </span>
                  </span>
                  <span className="max-md:hidden">
                    <GoesTo n={m} />
                  </span>
                  <IconButton
                    label={`Move ${c.name} up`}
                    disabled={i === 0}
                    className={cx(ROW_ICON, 'max-sm:size-11!')}
                    onClick={() => move(id, -1)}
                  >
                    <ArrowUp className="size-4" />
                  </IconButton>
                  <IconButton
                    label={`Move ${c.name} down`}
                    disabled={i === ids.length - 1}
                    className={cx(ROW_ICON, 'max-sm:size-11!')}
                    onClick={() => move(id, 1)}
                  >
                    <ArrowDown className="size-4" />
                  </IconButton>
                </li>
              )
            })}
          </ol>
        </ListFrame>
        <aside
          aria-label="What guests and waiters will see"
          className="space-y-3 rounded-card border border-border bg-surface p-4 shadow-card max-lg:hidden"
        >
          <b className="block text-sm">Guests and waiters will see</b>
          <p className="flex items-center gap-1.5 text-xs text-muted">
            <QrCode className="size-4" aria-hidden />
            QR menu sections, top to bottom
          </p>
          <ol className="space-y-1.5 text-sm">
            {ids.map((id, i) => (
              <li key={id} className="flex items-center gap-2.5">
                <span className="tabular grid size-6 place-items-center rounded-lg bg-soft text-xs font-bold text-muted">
                  {i + 1}
                </span>
                {byId.get(id)?.name}
              </li>
            ))}
          </ol>
          <p className="pt-1 text-xs text-muted">Order screen, left to right</p>
          <div className="flex flex-wrap gap-1.5">
            {ids.map((id) => (
              <span
                key={id}
                className="rounded-xl border border-border px-2.5 py-1 text-[13px] font-medium text-muted"
              >
                {byId.get(id)?.name}
              </span>
            ))}
          </div>
        </aside>
      </div>
    </div>
  )
}
